/**
 * EventDexGaps — "still missing from your dex" under a Calendar event card.
 *
 * Cross-references the event's featured species against the trainer's own dex
 * and renders a tile per gap, with the Quick-update choose-then-commit
 * mechanic (2026-08-24 ask; it deep-linked to the Pokédex search before): a
 * tap CHOOSES, one "Add N to my Nexus Dex" button commits, and a committed
 * tile taps back to exactly its prior state. The mechanic lives in
 * useGapCommit, shared with EventCatchUp and the Profile radar strips.
 *
 * The same tile as the Profile radar's gap strip, because it is the same
 * question asked on a different page. The page-level toggle (Calendar >
 * "What I'm missing") decides whether these render at all; this component
 * only handles ONE event.
 *
 * PRO, but TEASED. A free trainer still gets a row per event — it counts their
 * real gaps and withholds which ones ("You're still missing 6 from this event",
 * tap to open the paywall). The count is the hook; the identities are the
 * product. See the `locked` prop.
 *
 * (This briefly shipped as a Basic feature on 2026-08-07 and was put back behind
 * Pro the same day. The equivalent strip on the PROFILE page is deliberately
 * free — see ProfilePriorityWidgets' RaidRowWithGaps — so a trainer can see the
 * comparison working before being asked to pay for it on the events page.)
 */
import { useEffect, useMemo } from "react";
import { Lock } from "lucide-react";
import {
  CatchUpTile,
  GAP_TILE_ROW_CLASS,
  GAP_TILE_CELL_STYLE,
} from "@/components/EventCatchUp";
import { bindDexSyncApiFetch, getDexEntryState } from "@/lib/dexStateStore";
import { apiRequestRaw } from "@/lib/queryClient";
import { eventDexGaps } from "@/lib/eventDexGaps";
import { EvoLinesToggle } from "@/components/EvoLinesToggle";
import { useEvoLines } from "@/lib/evoLinesPref";
import { expandSpawnsToLines } from "@/lib/eventEvoLines";
import { sortGapsByVariant } from "@/lib/raidDexCrossRef";
import { isVariantAvailable } from "@/lib/variantAvailability";
import { isEventFormAvailable } from "@/lib/eventFormAvailability";
import { useSession } from "@/components/SessionProvider";
import { useGapCommit } from "@/hooks/useGapCommit";
import type { EventSpawn } from "@/lib/data";

/**
 * How many gaps one event card shows. An event can feature dozens of species
 * across several variants; the card is a strip, not a matrix, so we show the
 * first twelve and count the rest.
 */
const GAP_LIMIT = 12;

export function EventDexGaps({
  eventId,
  spawns,
  locked = false,
  impliedDynamax = false,
}: {
  eventId: string;
  spawns: readonly EventSpawn[];
  /** Max Battle / Max Monday event: featured species imply a Dynamax gap
   * check without an admin D-MAX tick (see raidDexGaps). */
  impliedDynamax?: boolean;
  /**
   * Free tier: count the gaps but withhold WHICH ones.
   *
   * A bare upsell that says nothing is easy to scroll past; a card that says
   * "you're still missing 6 from this event" is the feature arguing for itself,
   * from the trainer's own dex. The count is the hook, the identities are the
   * product — so the teaser is honest about the value without giving it away.
   * Tapping opens the paywall.
   */
  locked?: boolean;
}) {
  const { userPriorities, openPaywall } = useSession();

  // Commits made here must reach the server backup even when the Pokédex page
  // (the usual binder of the dex sync flusher) has not mounted this session.
  // Idempotent; mirrors the Profile radar block.
  useEffect(() => {
    bindDexSyncApiFetch(apiRequestRaw);
  }, []);

  // FROZEN per event set, the Quick-update rule (see useGapCommit): an added
  // species stops being a gap, so a live recompute would delete the tile just
  // committed and take its undo with it. The old DEX_STATE_EVENT re-read went
  // with the mechanic change — recomputing is the next visit's job.
  // Evo (2026-10-20): whole evolution lines on the strip, device-wide.
  const [evoLines] = useEvoLines();
  const { gaps, total, hasLinkedSpecies } = useMemo(
    () =>
      eventDexGaps(
        evoLines
          ? expandSpawnsToLines(spawns as Parameters<typeof eventDexGaps>[0])
          : (spawns as Parameters<typeof eventDexGaps>[0]),
        userPriorities.collectionFoci,
        (variant, speciesId, formId) =>
          getDexEntryState(variant, speciesId, formId)?.state === "present",
        // A form gap is available when the form itself is out (released, and
        // for a shiny tile the form has shiny art at all); species gaps keep
        // the per-variant roster check.
        (variant, speciesId, formId) =>
          formId
            ? isEventFormAvailable(variant, formId, speciesId)
            : isVariantAvailable(variant, speciesId),
        GAP_LIMIT,
        { impliedDynamax },
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [spawns, userPriorities.collectionFoci, impliedDynamax, evoLines],
  );

  // One flat row, variant-sorted (2026-10-12 owner ask). The card used to
  // stack a heading and a row per variant, which on a five-variant event was
  // five one-tile rows and most of the card's height. Now the tiles share one
  // wrapping row and each tile's badge, in its dex's colour, does the
  // labelling. The cap is applied upstream (eventDexGaps) on an already
  // variant-sorted list; the sort here only guards against a caller that
  // passed gaps unsorted.
  const row = useMemo(() => sortGapsByVariant(gaps), [gaps]);

  // Unconditional (hooks rule): called before the early returns below.
  const { tileState, tap, commit, undoAll, selectedCount, addedCount } = useGapCommit(gaps);

  /*
   * No species are attached to this event (a "generic" feed entry LeekDuck did
   * not list spawns for). There is nothing to cross-reference, so this strip
   * renders NOTHING.
   *
   * It used to say "No Pokémon linked to this event yet", which was at least
   * honest — the one thing it must never do is claim the trainer has everything
   * — but on a Calendar of a dozen events it repeated that non-answer down the
   * whole page. A line explaining why a strip is empty is still clutter, and the
   * absence says the same thing without spending a row on it (user direction,
   * 2026-08-14; the Profile "Friends are looking for:" block went the same way).
   *
   * The locked teaser below is deliberately downstream of this: an unlinked
   * event has nothing behind the upsell either.
   */
  if (!hasLinkedSpecies) return null;

  if (gaps.length === 0) {
    return (
      <p
        className="mt-2 text-[10.5px] text-[var(--text-secondary)]"
        data-testid={`event-dex-gaps-none-${eventId}`}
      >
        You already have everything from this event.
      </p>
    );
  }

  // The teaser. Deliberately AFTER the two states above: a trainer with nothing
  // linked, or nothing missing, is shown the plain truth rather than being sold
  // something that would tell them nothing. `total` is the uncapped count, so
  // the number is the real one and not the twelve-tile cap.
  if (locked) {
    return (
      <button
        type="button"
        onClick={() => openPaywall("event-dex-gaps")}
        data-testid={`event-dex-gaps-locked-${eventId}`}
        className="mt-2 w-full text-left rounded-xl p-2.5 flex items-center gap-2.5"
        style={{ background: "var(--surface-raised)", border: "1px solid var(--border-subtle)" }}
      >
        <span
          className="shrink-0 w-8 h-8 rounded-lg flex items-center justify-center"
          style={{ background: "var(--surface-overlay)" }}
          aria-hidden
        >
          <Lock className="w-3.5 h-3.5" style={{ color: "var(--text-secondary)" }} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[11.5px] font-semibold" style={{ color: "var(--text-primary)" }}>
            You're still missing {total} from this event
          </span>
          <span className="block text-[10.5px] mt-0.5" style={{ color: "var(--accent-fill)" }}>
            See which ones with Pro
          </span>
        </span>
      </button>
    );
  }

  return (
    <div className="mt-2" data-testid={`event-dex-gaps-${eventId}`}>
      <p className="text-[9px] font-bold uppercase tracking-[0.16em] text-[var(--text-tertiary)] mb-1.5 flex items-center justify-between gap-2">
        <span>Still missing from your dex · tap to choose</span>
        <EvoLinesToggle testId={`event-dex-gaps-evo-${eventId}`} />
      </p>
      {/* The tiles are the Quick-update card's own, in its five-across centred
          wrapping row, so idle / chosen / added read identically on every
          surface. Sorted by variant so the badge colours run in blocks. */}
      <ul className={GAP_TILE_ROW_CLASS} data-testid={`event-dex-gap-row-${eventId}`}>
        {row.map((g) => (
          <li
            key={`${g.variant}:${g.speciesId}:${g.formId ?? ""}`}
            className="min-w-0"
            style={GAP_TILE_CELL_STYLE}
          >
            <CatchUpTile
              variant={g.variant}
              speciesId={g.speciesId}
              formId={g.formId}
              state={tileState(g)}
              onToggle={() => tap(g)}
              testId={`event-dex-gap-${eventId}-${g.variant}-${g.speciesId}${g.formId ? `-${g.formId}` : ""}`}
            />
          </li>
        ))}
      </ul>

      {/* The commit. Only ever present when there is something to commit, so
          the strip is inert until the trainer has chosen something. */}
      {selectedCount > 0 && (
        <button
          type="button"
          onClick={commit}
          data-testid={`event-dex-gaps-commit-${eventId}`}
          className="mt-2 w-full rounded-full py-2 text-[12px] font-bold"
          style={{
            background: "var(--accent-fill)",
            color: "var(--text-on-accent)",
            minHeight: 40,
          }}
        >
          Add {selectedCount} to my Nexus Dex
        </button>
      )}

      {/* After a commit, the way back stays on the card rather than living
          only in a toast that has already gone. */}
      {selectedCount === 0 && addedCount > 0 && (
        <div className="mt-2 flex items-center justify-between gap-2">
          <p className="text-[10px]" style={{ color: "var(--text-tertiary)" }}>
            {addedCount} added. Tap one to take it back.
          </p>
          <button
            type="button"
            onClick={undoAll}
            data-testid={`event-dex-gaps-undo-${eventId}`}
            className="shrink-0 rounded-full px-3 py-1 text-[10px] font-bold"
            style={{
              background: "var(--surface-sunken)",
              color: "var(--text-secondary)",
              border: "1px solid var(--border-subtle)",
              minHeight: 32,
            }}
          >
            Undo all
          </button>
        </div>
      )}
      {total > gaps.length && (
        <p
          className="mt-1.5 text-[10px] text-[var(--text-tertiary)]"
          data-testid={`event-dex-gaps-more-${eventId}`}
        >
          +{total - gaps.length} more
        </p>
      )}
    </div>
  );
}

